import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    getAdminViewPermissions,
    type IPermissionActionDescriptor,
    type IUserAuthPDPModel,
    ResourceGroup,
    ResourceType,
} from '@experiences/interfaces';
import type { PropsWithChildren } from 'react';
import React, {
    createContext,
    useEffect,
    useMemo,
} from 'react';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import useSWR from 'swr';

import useUserInfo from '../../auth/hooks/UserInfo';
import { fetchUserAuthPDP } from '../../services/centralizedaccess/CentralizedAccessService';
import {
    updateAdminStatus,
    updateCanViewAdmin,
} from '../../store/action/UserProfileAction';
import {
    isOrgAdminSelector,
    isTenantAdminSelector,
    userGlobalId,
} from '../../store/selectors';

/** Exposed for route guards that must wait on PDP before denying access. */
export const UserAuthPDPModelContext = createContext<IUserAuthPDPModel | undefined>(undefined);

/**
 * The model alone cannot tell a guard why it is empty: still fetching and PDP unavailable both
 * read as `undefined`. Guards need to distinguish them, since one means "wait" and the other
 * means "we cannot answer" — blanking the page forever is not an option for either.
 */
export const UserAuthPDPStatusContext = createContext<{ isLoading: boolean; hasError: boolean }>({
    isLoading: false,
    hasError: false,
});

// TODO: DO NOT DELETE. This will be used in the future to get user permissions across admin
// const useGetUserPermissions = ({
//     resource, resourceType, action, tenantId = '',
// }: { resource: string; resourceType: string; action: AuthAction; tenantId?: string }) => {
//     const permissions = React.useContext(UserAuthPDPModelContext);

//     const filteredPermission = permissions?.actions?.filter((actionIterator) =>
//         actionIterator.ResourceGroup.toLowerCase() === resource.toLowerCase()
//         && actionIterator.ResourceType.toLowerCase() === resourceType.toLowerCase()
//         && actionIterator.ResourceAction.toUpperCase() === action.toUpperCase()
//         && actionIterator.Scope.toLowerCase().includes(tenantId.toLowerCase()));

//     if (!filteredPermission) {
//         return false;
//     }

//     return filteredPermission.length > 0;
// };

export const useHasAnyPermission = (
    requiredActions: IPermissionActionDescriptor[],
    tenantId = '',
): boolean => {
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const permissions = React.useContext(UserAuthPDPModelContext);

    if (isOrgAdmin) {
        return true;
    }

    if (!permissions?.actions || requiredActions.length === 0) {
        return false;
    }

    return requiredActions.some(({
        resource, resourceType, action,
    }) =>
        permissions.actions.some((perm) =>
            perm.ResourceGroup.toLowerCase() === resource.toLowerCase()
            && perm.ResourceType.toLowerCase() === resourceType.toLowerCase()
            && perm.ResourceAction.toUpperCase() === action.toUpperCase()
            && perm.Scope.toLowerCase().includes(tenantId.toLowerCase())
        )
    );
};

const OrganizationScopeKey = '/';
const TenantScopeKeyPrefix = '/tenant/';

/**
 * Scope-correct permission check: mirrors what the authorization policy does, which is to match an
 * assignment when `startswith(requestedScope, grantedScopeKey)`.
 *
 * Prefer this over {@link useHasAnyPermission} for anything security relevant. That one substring
 * matches the *granted* scope, which gets both directions wrong: with no tenant passed it defaults
 * to `''`, and `includes('')` accepts a grant at any scope; with a tenant passed it rejects an
 * organization-wide grant of a tenant-scoped action that the API would allow.
 */
export const useHasAnyPermissionAtScope = (
    requiredActions: IPermissionActionDescriptor[],
    tenantId?: string,
): boolean => {
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const permissions = React.useContext(UserAuthPDPModelContext);

    if (isOrgAdmin) {
        return true;
    }

    if (!permissions?.actions || requiredActions.length === 0) {
        return false;
    }

    const requestedScope = tenantId
        ? `${TenantScopeKeyPrefix}${tenantId.toLowerCase()}`
        : OrganizationScopeKey;

    return requiredActions.some(({
        resource, resourceType, action,
    }) =>
        permissions.actions.some((perm) =>
            perm.ResourceGroup.toLowerCase() === resource.toLowerCase()
            && perm.ResourceType.toLowerCase() === resourceType.toLowerCase()
            && perm.ResourceAction.toUpperCase() === action.toUpperCase()
            // An empty granted scope would prefix-match everything, so it must never grant.
            && !!perm.Scope
            && requestedScope.startsWith(perm.Scope.toLowerCase())
        )
    );
};

export const UserAuthPDPModelProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const EnableTenantAdminControl = useFeatureFlagValue(Features.EnableTenantAdminControl.name);

    const { token } = useUserInfo();

    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const isTenantAdmin = useSelector(isTenantAdminSelector);
    /** Align with server PDP calls (`SecurableId: req.userId`) so user-assigned roles (e.g. Licensing viewer) resolve. */
    const userId = useSelector(userGlobalId);

    const dispatch = useDispatch();

    const {
        data: userAuthPDPModel, isLoading, error,
    } = useSWR(
        token && userId ? {
            url: `/pap_/api/opa/centralizedaccess/getallactions`,
            payload: {
                RequestParams: {
                    Scope: '/',
                    SecurableId: userId,
                },
            },
        } : null,
        fetchUserAuthPDP
    );

    useEffect(() => {
        // Tenant-admin bootstrap (AdministrationPage VIEW) — unrelated to Licensing viewer; gated by EnableTenantAdminControl.
        if (EnableTenantAdminControl && !isOrgAdmin && !isTenantAdmin && userAuthPDPModel?.actions?.some(action =>
            action.ResourceGroup.toLowerCase() === ResourceGroup.CentralizedAccess
            && action.ResourceType.toLowerCase() === ResourceType.AdministrationPage
            && action.ResourceAction.toLowerCase() === 'view')) {
            dispatch(updateAdminStatus('TENANT_ADMIN'));
        // if data is no longer loading and userAuthPDPModel is still null, then dispatch updateAdminStatus
        } else if (!isLoading && !userAuthPDPModel && error) {
            dispatch(updateAdminStatus());
        }
    }, [ EnableTenantAdminControl, dispatch, error, isLoading, isOrgAdmin, isTenantAdmin, userAuthPDPModel ]);

    const adminViewPermissions = useMemo(() => getAdminViewPermissions(), []);

    const canViewAdmin = useMemo(() => {
        if (!userAuthPDPModel?.actions) {
            return false;
        }
        return adminViewPermissions.some(({
            resource, resourceType, action,
        }) =>
            userAuthPDPModel.actions.some((perm) =>
                perm.ResourceGroup.toLowerCase() === resource.toLowerCase()
                && perm.ResourceType.toLowerCase() === resourceType.toLowerCase()
                && perm.ResourceAction.toUpperCase() === action.toUpperCase()
            )
        );
    }, [ adminViewPermissions, userAuthPDPModel ]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        dispatch(updateCanViewAdmin(canViewAdmin));
    }, [ canViewAdmin, dispatch, isLoading ]);

    const pdpStatus = useMemo(() => ({
        isLoading,
        hasError: !!error,
    }), [ isLoading, error ]);

    // If error, short circuit and return children
    return <UserAuthPDPModelContext.Provider value={userAuthPDPModel}>
        <UserAuthPDPStatusContext.Provider value={pdpStatus}>
            {isOrgAdmin || isTenantAdmin || error
                ? children
                : (
                    isLoading && isTenantAdmin == null
                        ? undefined
                        : children
                )}
        </UserAuthPDPStatusContext.Provider>
    </UserAuthPDPModelContext.Provider>;
};
